import { DashboardData } from 'types/dashboard';

export interface StatBadge {
  value: number;
  percentChange: number;
  direction: 'up' | 'down' | 'none';
  showFire: boolean;
}

export interface InsightsData {
  categories: string[];
  data: number[];
}

export interface AnalysisData {
  categories: string[];
  data: number[];
}

export interface InsightsCharts {
  followerData: InsightsData;
  followingData: InsightsData;
  mediaData: InsightsData;
  analysisData: AnalysisData;
}

export const calculateTimeRemaining = (dateTime: string): { value: number; isMinutes: boolean } => {
  const targetTime = new Date(dateTime).getTime();
  const now = new Date().getTime();
  const diffMs = targetTime - now;
  const minutes = Math.max(0, Math.round(diffMs / (1000 * 60)));
  if (minutes < 60) return { value: minutes, isMinutes: true };
  return { value: Math.round(minutes / 60), isMinutes: false };
};

export const filterInsightsByDateRange = (
  insights: DashboardData['insights'],
  dateFilter: string,
): NonNullable<DashboardData['insights']> => {
  if (!insights || insights.length === 0) return [];
  const now = new Date();
  let cutoffDate = new Date(0);
  switch (dateFilter) {
    case 'Today': cutoffDate = new Date(now.setHours(0, 0, 0, 0)); break;
    case 'Last 7 days': cutoffDate = new Date(now.setDate(now.getDate() - 7)); break;
    case 'Last 30 days': cutoffDate = new Date(now.setDate(now.getDate() - 30)); break;
    default: return insights;
  }
  return insights.filter((insight) => new Date(insight.created_at) >= cutoffDate);
};

export const processViewsData = (
  dashboardData: DashboardData['data'],
  i18nLang: string,
): AnalysisData => {
  if (!dashboardData || dashboardData.length <= 1) return { categories: [], data: [] };
  const entries = dashboardData.slice(1) as Array<{ Date: string; Likes?: number; Messages?: number; Comments?: number; Followings?: number; Posts?: number; StoryViews?: number }>;
  const sorted = [...entries].sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());
  return {
    categories: sorted.map((e) => new Date(e.Date).toLocaleDateString(i18nLang || 'en', { month: 'short', day: 'numeric' })),
    data: sorted.map((e) => (e.Likes || 0) + (e.Comments || 0) + (e.Followings || 0) + (e.Messages || 0) + (e.Posts || 0) + (e.StoryViews || 0)),
  };
};

export const processInsightsData = (
  insights: DashboardData['insights'],
  dashboardData: DashboardData['data'],
  dateFilter: string,
  i18nLang: string,
): InsightsCharts => {
  const analysisData = processViewsData(dashboardData, i18nLang);
  if (!insights || insights.length === 0) {
    return {
      followerData: { categories: [], data: [] },
      followingData: { categories: [], data: [] },
      mediaData: { categories: [], data: [] },
      analysisData,
    };
  }
  const filteredInsights = filterInsightsByDateRange(insights, dateFilter);
  const latestInsightsMap = new Map<string, NonNullable<DashboardData['insights']>[number]>();
  const getDateKey = (date: string): string => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  filteredInsights.forEach((insight) => {
    const dateKey = getDateKey(insight.created_at);
    if (!latestInsightsMap.has(dateKey)) {
      latestInsightsMap.set(dateKey, insight);
    } else {
      const existing = latestInsightsMap.get(dateKey);
      if (existing && new Date(insight.created_at).getTime() > new Date(existing.created_at).getTime()) {
        latestInsightsMap.set(dateKey, insight);
      }
    }
  });
  const sortedEntries = Array.from(latestInsightsMap.entries()).sort((a, b) => new Date(a[1].created_at).getTime() - new Date(b[1].created_at).getTime());
  const categories = sortedEntries.map(([dateKey]) => new Date(dateKey).toLocaleDateString(i18nLang || 'en', { month: 'short', day: 'numeric' }));
  return {
    followerData: { categories, data: sortedEntries.map(([, i]) => i.follower_count || 0) },
    followingData: { categories, data: sortedEntries.map(([, i]) => i.following_count || 0) },
    mediaData: { categories, data: sortedEntries.map(([, i]) => i.media_count || 0) },
    analysisData,
  };
};

// Compute badge for a stat by comparing current vs previous value
export const computeBadge = (current: number, previous: number): StatBadge => {
  if (previous === 0 && current === 0) return { value: 0, percentChange: 0, direction: 'none', showFire: false };
  if (previous === 0) return { value: current, percentChange: 100, direction: 'up', showFire: current > 0 };
  const diff = current - previous;
  const percentChange = Math.round((diff / previous) * 100);
  const direction: 'up' | 'down' | 'none' = diff > 0 ? 'up' : diff < 0 ? 'down' : 'none';
  const showFire = percentChange >= 70;
  return { value: diff, percentChange: Math.abs(percentChange), direction, showFire };
};

// ─── Calendar helpers ───
export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export const getCalendarDays = (month: number, year: number): (number | null)[] => {
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const startOffset = firstDay === 0 ? 6 : firstDay - 1; // Mon-based offset
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: (number | null)[] = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return days;
};

export const toDateStr = (day: number, month: number, year: number): string => {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};
